import React, { useState, useEffect, useRef } from "react";
import { Modal, IconButton, PrimaryButton, DefaultButton } from "@fluentui/react";
import { Icon } from "@fluentui/react/lib/Icon";
import styles from "./ContextModal.module.css";

interface ContextModalProps {
    isOpen: boolean;
    onDismiss: () => void;
    onSubmit: (context: string) => void;
    initialContext?: string;
}

// Suggestion chips
const SUGGESTION_CHIPS = [
    "Me encantan los carros deportivos",
    "Prefiero SUV compacta",
    "Mi presupuesto es US$8,000",
    "Viajo seguido a zonas con caminos de tierra",
    "Prefiero bajo consumo de combustible",
    "Me gustan Toyota y Honda",
    "Necesito espacio para familia de 5",
    "Uso principalmente en ciudad",
    "Busco carro para trabajo",
    "Necesito buen sistema de aire acondicionado"
];

// Quick templates
const QUICK_TEMPLATES = [
    {
        id: "family",
        title: "Familia práctica",
        text: "Tengo familia de 5, presupuesto US$9,000, uso diario en Managua y viajes a la playa; prefiero automático y repuestos fáciles en NI."
    },
    {
        id: "efficient",
        title: "Ahorro y ciudad",
        text: "Me gustan los sedanes japoneses, bajo consumo, presupuesto US$7,500, trayectos cortos y tráfico pesado, necesito buen A/C."
    },
    {
        id: "adventurous",
        title: "Aventurero",
        text: "Busco SUV compacta con buen despeje para caminos de tierra, presupuesto US$10,000, confiable y con repuestos accesibles."
    }
];

export const ContextModal: React.FC<ContextModalProps> = ({
    isOpen,
    onDismiss,
    onSubmit,
    initialContext = ""
}) => {
    const [contextText, setContextText] = useState<string>(initialContext);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const closeButtonRef = useRef<any>(null);
    const modalRef = useRef<HTMLDivElement>(null);

    const MAX_CHARS = 3000;
    const remainingChars = MAX_CHARS - contextText.length;

    // Focus management
    useEffect(() => {
        if (isOpen && closeButtonRef.current) {
            setTimeout(() => {
                try {
                    if (closeButtonRef.current && typeof closeButtonRef.current.focus === 'function') {
                        closeButtonRef.current.focus();
                    }
                } catch (e) {
                    modalRef.current?.focus();
                }
            }, 100);
        }
    }, [isOpen]);

    // Handle ESC key
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onDismiss();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEsc);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = '';
        };
    }, [isOpen, onDismiss]);

    const handleChipClick = (chipText: string) => {
        const newText = contextText ? `${contextText} ${chipText}` : chipText;
        if (newText.length <= MAX_CHARS) {
            setContextText(newText);
            textAreaRef.current?.focus();
        }
    };

    const handleTemplateClick = (templateText: string) => {
        if (templateText.length <= MAX_CHARS) {
            setContextText(templateText);
            textAreaRef.current?.focus();
        }
    };

    const handleSubmit = () => {
        onSubmit(contextText);
        onDismiss();
    };

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newText = e.target.value;
        if (newText.length <= MAX_CHARS) {
            setContextText(newText);
        }
    };

    if (!isOpen) return null;

    return (
        <Modal
            isOpen={isOpen}
            onDismiss={onDismiss}
            isBlocking={true}
            containerClassName={styles.modalContainer}
            styles={{
                main: {
                    backgroundColor: '#1e1e1e',
                    borderRadius: '12px',
                    border: '1px solid #333',
                    width: '90vw',
                    maxWidth: '900px',
                    height: '90vh',
                    maxHeight: '850px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }
            }}
        >
            <div
                ref={modalRef}
                className={styles.modalContent}
                role="dialog"
                aria-modal="true"
                aria-labelledby="context-modal-title"
            >
                <div className={styles.modalHeader}>
                    <h2 id="context-modal-title" className={styles.modalTitle}>
                        <span className={styles.titleIcon}>💬</span>
                        Cuéntanos más (opcional)
                    </h2>
                    <IconButton
                        componentRef={closeButtonRef}
                        className={styles.closeButton}
                        iconProps={{ iconName: 'Cancel' }}
                        ariaLabel="Cerrar"
                        onClick={onDismiss}
                    />
                </div>

                <p className={styles.subtitle}>
                    Estos detalles NO son filtros, solo ayudan al copiloto a aconsejarte mejor.
                </p>

                <div className={styles.modalBody}>
                    {/* Guide Box */}
                    <div className={styles.guideBox}>
                        <h3 className={styles.guideTitle}>
                            <Icon iconName="Lightbulb" className={styles.guideIcon} />
                            Ideas de qué compartir:
                        </h3>
                        <div className={styles.guideList}>
                            <div className={styles.guideItem}>
                                <span className={styles.guideEmoji}>💰</span>
                                <span>Presupuesto aproximado y si contemplas financiamiento.</span>
                            </div>
                            <div className={styles.guideItem}>
                                <span className={styles.guideEmoji}>🚗</span>
                                <span>Uso principal (ciudad, carretera, caminos de tierra) y distancia típica.</span>
                            </div>
                            <div className={styles.guideItem}>
                                <span className={styles.guideEmoji}>❤️</span>
                                <span>Preferencias (tipo de carro, transmisión, marcas que te gustan o evitarías).</span>
                            </div>
                            <div className={styles.guideItem}>
                                <span className={styles.guideEmoji}>🔧</span>
                                <span>Costos de mantenimiento, consumo de combustible, repuestos en Nicaragua.</span>
                            </div>
                            <div className={styles.guideItem}>
                                <span className={styles.guideEmoji}>📱</span>
                                <span>Tecnología o seguridad (cámara, CarPlay/Android Auto, aire acondicionado).</span>
                            </div>
                            <div className={styles.guideItem}>
                                <span className={styles.guideEmoji}>🔄</span>
                                <span>Horizonte de tiempo (años que planeas conservarlo y facilidad de reventa).</span>
                            </div>
                        </div>
                    </div>

                    {/* Quick Templates */}
                    <div className={styles.templatesSection}>
                        <h3 className={styles.sectionTitle}>Plantillas rápidas</h3>
                        <div className={styles.templatesGrid}>
                            {QUICK_TEMPLATES.map((template) => (
                                <button
                                    key={template.id}
                                    className={styles.templateCard}
                                    onClick={() => handleTemplateClick(template.text)}
                                    aria-label={`Usar plantilla: ${template.title}`}
                                >
                                    <div className={styles.templateTitle}>{template.title}</div>
                                    <div className={styles.templateText}>{template.text}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Suggestion Chips */}
                    <div className={styles.chipsSection}>
                        <h3 className={styles.sectionTitle}>Sugerencias rápidas</h3>
                        <div className={styles.chipsContainer}>
                            {SUGGESTION_CHIPS.map((chip, index) => (
                                <button
                                    key={index}
                                    className={styles.chip}
                                    onClick={() => handleChipClick(chip)}
                                    aria-label={`Agregar: ${chip}`}
                                >
                                    <Icon iconName="Add" className={styles.chipIcon} />
                                    {chip}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Text Area */}
                    <div className={styles.textAreaSection}>
                        <h3 className={styles.sectionTitle}>Tu contexto</h3>
                        <textarea
                            ref={textAreaRef}
                            className={styles.textArea}
                            value={contextText}
                            onChange={handleTextChange}
                            placeholder="Ej.: Presupuesto US$8,000; uso diario en Managua; caminos de tierra los fines de semana; prefiero japonés, automático; repuestos fáciles."
                            rows={6}
                            maxLength={MAX_CHARS}
                            aria-label="Escribe tu contexto aquí"
                        />
                        <div className={styles.charCounter}>
                            <span className={remainingChars < 100 ? styles.charCounterWarning : ''}>
                                {remainingChars} caracteres restantes
                            </span>
                        </div>
                    </div>

                    {/* Clarification Message */}
                    <div className={styles.clarification}>
                        <Icon iconName="Info" className={styles.clarificationIcon} />
                        <span>Esta información no limita tus opciones, solo ayuda al copiloto a entenderte mejor.</span>
                    </div>
                </div>

                <div className={styles.modalFooter}>
                    <PrimaryButton
                        text="Usar con el copiloto 🚀"
                        onClick={handleSubmit}
                        className={styles.primaryButton}
                        iconProps={{ iconName: 'Send' }}
                    />
                    <DefaultButton
                        text="Cancelar"
                        onClick={onDismiss}
                        className={styles.secondaryButton}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default ContextModal;